import { Nullable } from '@ps/utils';

export type FeasibilityAdvancedFilterType = 'include' | 'exclude';

export type FeasibilityAdvancedFilterCriteriaType =
  | 'projectManager'
  | 'billingNumber'
  | 'template'
  | 'surveyTitle';

/**
 * The two criteria types whose values are found by typing into the search field.
 */
export type FeasibilityCriteriaSearchType = Extract<
  FeasibilityAdvancedFilterCriteriaType,
  'billingNumber' | 'surveyTitle'
>;

export interface FeasibilityAdvancedFiltersDateRange {
  readonly start: Nullable<string>;
  readonly end: Nullable<string>;
}

export interface FeasibilityFilterCriterion {
  readonly id: string;
  readonly name: string;
}

export interface FeasibilityFilterCriteria {
  readonly type: FeasibilityAdvancedFilterCriteriaType;
  readonly name: string;
  readonly values: FeasibilityFilterCriterion[];
}

/**
 * A survey's committed Advanced Filters — one type for one concept at two
 * lifecycle stages: what the insights slice holds while a survey is being
 * created or edited, and what the survey itself persists.
 *
 * This is source for Survey's feasibility fields, and for Feasibility recalculation fields.
 */
export interface AdvancedFeasibilityFilters {
  type: FeasibilityAdvancedFilterType;
  dateRange: FeasibilityAdvancedFiltersDateRange;
  criteria: FeasibilityFilterCriteria[];
  /** Found by the criteria + date range lookup. */
  matchedGroupIds: number[];
  /** Hand-picked in the Groups field, independent of the criteria. */
  manualGroupIds: number[];
  respondentStatuses: number[];
  excludeFromFeasibleAudience: boolean;
  /** Include mode only — the PSIDs resolved for the all groups. */
  psidRefs: string[];
}

//SECTION Advanced Filters Params

export interface DateRangeFilterParams {
  start: Date;
  end: Date;
}

/**
 * What narrows every Advanced Filters lookup, wherever it is dispatched from.
 */
export interface AdvancedFiltersLookupScope {
  dateRange: Nullable<DateRangeFilterParams>;
  countryCode: string;
  languageCode: string;
  filterType: FeasibilityAdvancedFilterType;
}

/** Finds the prior surveys matching the committed criteria — the matched groups. */
export interface FindSurveysParams extends AdvancedFiltersLookupScope {
  criteria: FeasibilityFilterCriteria[];
}

/** Typeahead for the Billing Number / Survey Title criteria field. */
export interface SearchCriteriaSurveysParams
  extends AdvancedFiltersLookupScope {
  searchType: FeasibilityCriteriaSearchType;
  value: string;
}

/** Typeahead for the Groups field. */
export interface SearchGroupSurveysParams extends AdvancedFiltersLookupScope {
  value: string;
}

/**
 * What a typeahead searches by. The Groups field searches surveys by id; the criteria field
 * searches by whichever criteria type is active.
 */
export type SurveySearchType = FeasibilityCriteriaSearchType | 'group';

/** Any typeahead survey search — the criteria field's or the Groups field's. */
export interface SurveySearchParams extends AdvancedFiltersLookupScope {
  searchType: SurveySearchType;
  value: string;
}

/** Counts the respondents the current audience filter covers. */
export interface RespondentCountsParams {
  filterType: FeasibilityAdvancedFilterType;
  allGroupIds: number[];
  statuses: number[];
}
//!SECTION
